import React, { useState, useEffect, useCallback, useMemo, memo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { ShoppingCart, Heart, Eye, MessageSquare, Star, Edit, Zap } from 'lucide-react';
import Price from '@/components/Price';
import { generateProductSlug } from '@/utils';
import { toast } from 'sonner';

// Stable placeholder — generated once per unique name, not on every render
const placeholderCache = new Map();
const getPlaceholder = (name) => {
  if (placeholderCache.has(name)) return placeholderCache.get(name);
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="400"><rect width="100%" height="100%" fill="#f8fafc"/><text x="50%" y="44%" font-family="sans-serif" font-size="14" fill="#cbd5e1" dominant-baseline="middle" text-anchor="middle">No Image</text><text x="50%" y="56%" font-family="sans-serif" font-size="12" fill="#e2e8f0" dominant-baseline="middle" text-anchor="middle">${(name || '').slice(0, 20)}</text></svg>`;
  const uri = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  if (placeholderCache.size < 200) placeholderCache.set(name, uri);
  return uri;
};

const StarRating = memo(({ rating, size = 'sm' }) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
  const starSize = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';

  return (
    <div className="flex items-center gap-0.5">
      {[...Array(fullStars)].map((_, i) => (
        <Star key={`full-${i}`} className={`${starSize} fill-amber-400 text-amber-400`} />
      ))}
      {hasHalfStar && (
        <Star className={`${starSize} fill-amber-400/50 text-amber-400`} />
      )}
      {[...Array(emptyStars)].map((_, i) => (
        <Star key={`empty-${i}`} className={`${starSize} text-gray-200`} />
      ))}
    </div>
  );
});

const ProductCard = memo(({ product, index = 0, forceShowPrice = false, viewMode = 'grid' }) => {
  const { addToCart, settings, user } = useApp();
  const navigate = useNavigate();
  const showPrices = forceShowPrice || settings?.show_prices !== false;
  const isOffer = product.is_weekly_offer;
  const displayPrice = product.price;

  // Stable placeholder — never changes for the same product name
  const placeholderSvg = useMemo(() => getPlaceholder(product.name), [product.name]);

  // Stable image src — only use placeholder if no images
  const imgSrc = product.images?.[0] || placeholderSvg;

  // Read wishlist once from localStorage — no effect, no re-render on mount
  const [isWishlisted, setIsWishlisted] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('gpgt_wishlist') || '[]');
      return saved.includes(product.id);
    } catch {
      return false;
    }
  });

  const handleWishlist = useCallback((e) => {
    e.stopPropagation();
    try {
      const saved = JSON.parse(localStorage.getItem('gpgt_wishlist') || '[]');
      let updated;
      if (saved.includes(product.id)) {
        updated = saved.filter(id => id !== product.id);
        setIsWishlisted(false);
        toast('Removed from wishlist', { icon: '💔' });
      } else {
        updated = [...saved, product.id];
        setIsWishlisted(true);
        toast.success('Added to wishlist! ❤️');
      }
      localStorage.setItem('gpgt_wishlist', JSON.stringify(updated));
    } catch (err) {
      toast.error('Could not update wishlist');
    }
  }, [product.id]);

  const handleCardClick = useCallback(() => {
    navigate(`/products/${generateProductSlug(product.name, product.id, product.slug)}`);
  }, [navigate, product.name, product.id, product.slug]);

  const handleCartClick = useCallback((e) => {
    e.stopPropagation();
    if (product.stock === 0) {
      navigate(`/products/${generateProductSlug(product.name, product.id, product.slug)}`);
    } else {
      addToCart(product.id);
    }
  }, [product.stock, product.id, product.name, product.slug, navigate, addToCart]);

  return (
    <div
      className={`group relative bg-white rounded-xl border border-gray-100 overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:border-gray-200 ${viewMode === 'list' ? 'flex flex-row items-stretch' : 'flex flex-col'}`}
      onClick={handleCardClick}
      data-testid={`product-card-${product.id}`}
    >
      {/* Image Container — aspect ratio via CSS, no paddingTop hack */}
      <div
        className={`relative bg-gray-50 flex-shrink-0 overflow-hidden ${viewMode === 'list' ? 'w-32 sm:w-48 border-r border-gray-100' : 'w-full aspect-square'}`}
      >
        <img
          src={imgSrc}
          alt={product.name}
          loading={index < 4 ? "eager" : "lazy"}
          className="w-full h-full object-contain p-3 transition-transform duration-500 group-hover:scale-105"
          style={{ willChange: 'transform' }}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = placeholderSvg;
          }}
        />

        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300 pointer-events-none" />

        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-col gap-1.5 pointer-events-none">
          {isOffer && (
            <span className="inline-flex items-center gap-1 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
              <Zap className="h-2.5 w-2.5" />
              {product.specifications?._offer_tag || 'SPECIAL OFFER'}
            </span>
          )}
          {product.is_featured && (
            <span className="inline-flex items-center bg-[#0c1b44] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm">
              FEATURED
            </span>
          )}
        </div>

        {/* Action pills - top right */}
        <div className="absolute top-2 right-2 flex flex-col gap-1.5 opacity-0 group-hover:opacity-100 transition-all duration-200 translate-x-2 group-hover:translate-x-0">
          <button
            className={`w-8 h-8 rounded-full flex items-center justify-center shadow-md transition-colors border border-gray-100 ${
              isWishlisted ? 'bg-red-50 text-red-500 hover:bg-red-100' : 'bg-white hover:bg-[#0c1b44] hover:text-white'
            }`}
            onClick={handleWishlist}
            title={isWishlisted ? 'Remove from Wishlist' : 'Add to Wishlist'}
          >
            <Heart className={`h-3.5 w-3.5 ${isWishlisted ? 'fill-current' : ''}`} />
          </button>
          <button
            className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-[#0c1b44] hover:text-white transition-colors border border-gray-100"
            onClick={(e) => { e.stopPropagation(); handleCardClick(); }}
            title="Quick View"
          >
            <Eye className="h-3.5 w-3.5" />
          </button>
          {user?.role === 'admin' && (
            <button
              className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md hover:bg-amber-500 hover:text-white transition-colors border border-gray-100"
              onClick={(e) => { e.stopPropagation(); navigate(`/admin/products?edit=${product.id}`); }}
              title="Edit (Admin)"
            >
              <Edit className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Add to Cart slide-up (only in grid mode) */}
        {viewMode === 'grid' && (
          <div className="absolute bottom-0 left-0 right-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
            <button
              className="w-full py-2.5 text-white text-sm font-semibold flex items-center justify-center gap-2 bg-[#0c1b44] hover:bg-[#1a2d64] transition-colors"
              onClick={handleCartClick}
              data-testid={`add-to-cart-${product.id}`}
            >
              {product.stock === 0 ? (
                <><MessageSquare className="h-4 w-4" /> Get Quote</>
              ) : showPrices ? (
                <><ShoppingCart className="h-4 w-4" /> Add to Cart</>
              ) : (
                <><MessageSquare className="h-4 w-4" /> Add to Inquiry</>
              )}
            </button>
          </div>
        )}

      </div>

      {/* Info Section */}
      <div className={`p-3 sm:p-4 flex flex-col justify-center ${viewMode === 'list' ? 'flex-1' : ''}`}>
        {/* Brand */}
        <p className="text-[10px] text-gray-400 uppercase tracking-wider font-semibold mb-1 truncate">
          {product.brand || 'General'}
        </p>

        {/* Product Name */}
        <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-2 group-hover:text-[#0c1b44] transition-colors leading-snug" style={{ minHeight: '2.5rem' }}>
          {product.name}
        </h3>

        {/* Rating */}
        {product.review_count > 0 && (
          <div className="flex items-center gap-1.5 mb-2">
            <StarRating rating={product.average_rating || 0} />
            <span className="text-[10px] text-gray-400">({product.review_count})</span>
          </div>
        )}

        {/* Price */}
        <div className="flex items-center justify-between">
          {(showPrices || displayPrice > 1) ? (
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-base font-bold text-[#0c1b44]" data-testid={`price-${product.id}`}>
                <Price amount={displayPrice} size="md" />
              </span>
            </div>
          ) : (
            <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-1 rounded-full" data-testid={`request-quote-${product.id}`}>
              Request Quote
            </span>
          )}

          {/* Low stock warning */}
          {product.stock > 0 && product.stock <= 5 && (
            <span className="text-[10px] text-orange-500 font-medium">
              Only {product.stock} left
            </span>
          )}
        </div>

        {viewMode === 'list' && (
          <div className="mt-4 hidden sm:block">
            <button
              className="px-6 py-2 rounded-lg text-white text-sm font-semibold flex items-center justify-center gap-2 bg-[#0c1b44] hover:bg-[#1a2d64] transition-colors"
              onClick={handleCartClick}
            >
              {product.stock === 0 ? (
                <><MessageSquare className="h-4 w-4" /> Get Quote</>
              ) : showPrices ? (
                <><ShoppingCart className="h-4 w-4" /> Add to Cart</>
              ) : (
                <><MessageSquare className="h-4 w-4" /> Add to Inquiry</>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
});

export { StarRating };
export default ProductCard;
